import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Save, Power } from 'lucide-react';
import { noticeApi } from '../../utils/api';

// Website khulepachhi dekhine notice modal — photo + title clickable + link
function AdminNotice() {
  const [notices, setNotices] = useState([]);
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const res = await noticeApi.getAll();
      setNotices(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle('');
    setLink('');
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview('');
    setRemoveImage(false);
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setTitle(n.title || '');
    setLink(n.link || '');
    setFile(null);
    setRemoveImage(false);
    setPreview(n.image || '');
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setRemoveImage(false);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await noticeApi.update(editingId, { title, link, removeImage }, file);
        toast.success('नोटिस अद्यावधिक भयो।');
      } else {
        await noticeApi.create({ title, link, active: 'true' }, file);
        toast.success('नयाँ नोटिस सक्रिय भयो — website khulda dekhincha।');
      }
      resetForm();
      await fetchNotices();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'सुरक्षित गर्न असफल।');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (n) => {
    try {
      await noticeApi.update(n.id, { active: n.active ? 'false' : 'true' });
      await fetchNotices();
      toast.success(n.active ? 'नोटिस निष्क्रिय भयो।' : 'नोटिस सक्रिय भयो।');
    } catch {
      toast.error('असफल।');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('नोटिस मेटाउन निश्चित?')) return;
    try {
      await noticeApi.remove(id);
      if (editingId === id) resetForm();
      await fetchNotices();
      toast.success('मेटियो।');
    } catch {
      toast.error('मेट्न असफल।');
    }
  };

  if (loading) return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;

  const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft';
  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm outline-none bg-white focus:border-crimson';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-2xl">नोटिस मोडल</h2>
          <p className="text-[12.5px] text-muted mt-0.5">Website khulepachhi sabai lai dekhine notice — photo + title clickable, link ma redirect hunchha।</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark disabled:opacity-60 transition-colors">
          <Save size={15} /> {saving ? 'सुरक्षित...' : editingId ? 'अद्यावधिक गर्नुहोस्' : 'नयाँ नोटिस सक्रिय गर्नुहोस्'}
        </button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[1fr_360px] items-start">
        {/* Form */}
        <div className="bg-white border border-line rounded-[14px] p-5 md:p-6 flex flex-col gap-4">
          <label className={labelCls}>
            शीर्षक (title)
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="जस्तै: दशैं धमाका अफर!" />
          </label>
          <label className={labelCls}>
            रिडाइरेक्ट लिंक (click garda janane)
            <input className={inputCls} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://... वा /article/12" />
          </label>
          <label className={labelCls}>
            फोटो
            <input type="file" accept="image/*" onChange={handleFile} className={`${inputCls} py-2`} />
          </label>

          {(preview || (editingId && notices.find((n) => n.id === editingId)?.image)) && !removeImage && (
            <img src={preview} alt="notice preview" className="max-h-56 rounded-xl border border-line object-contain" />
          )}
          {removeImage && <p className="text-[12px] font-bold text-crimson">फोटो हटाइनेछ।</p>}
          {preview && !editingId && (
            <button type="button" onClick={() => { setFile(null); setPreview(''); }} className="text-[12px] font-bold text-crimson w-fit hover:underline">
              फोटो हटाउनुहोस्
            </button>
          )}
          {editingId && notices.find((n) => n.id === editingId)?.image && (
            <button type="button" onClick={() => setRemoveImage((r) => !r)} className="text-[12px] font-bold text-crimson w-fit hover:underline">
              {removeImage ? 'फोटो राख्नुहोस्' : 'फोटो हटाउनुहोस्'}
            </button>
          )}
        </div>

        {/* Existing notices list */}
        <div className="bg-white border border-line rounded-[14px] p-5">
          <h3 className="font-display text-lg mb-3">सबै नोटिसहरू</h3>
          {notices.length === 0 && <p className="text-[13px] text-muted">कुनै नोटिस छैन।</p>}
          <div className="flex flex-col gap-3">
            {notices.map((n) => (
              <div key={n.id} className={`border rounded-xl p-3 ${n.active ? 'border-green-400 bg-green-50/50' : 'border-line'}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-[13px] font-bold text-ink truncate">{n.title || '(शीर्षक छैन)'}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${n.active ? 'bg-green-500 text-white' : 'bg-paper-tint text-muted'}`}>
                    {n.active ? 'ACTIVE' : 'OFF'}
                  </span>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <button type="button" onClick={() => startEdit(n)} className="text-[11.5px] font-semibold border border-line rounded-lg px-2.5 py-1.5 hover:border-crimson hover:text-crimson transition-colors">
                    सम्पादन
                  </button>
                  <button type="button" onClick={() => toggleActive(n)} className="inline-flex items-center gap-1 text-[11.5px] font-semibold border border-line rounded-lg px-2.5 py-1.5 hover:border-green-600 hover:text-green-700 transition-colors">
                    <Power size={11} /> {n.active ? 'निष्क्रिय' : 'सक्रिय'}
                  </button>
                  <button type="button" onClick={() => handleDelete(n.id)} className="text-[11.5px] font-semibold border border-line rounded-lg px-2.5 py-1.5 text-crimson hover:bg-crimson hover:text-white transition-colors">
                    मेट्नुहोस्
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminNotice;